import type { ProjectPath, TaskView } from "../api/generated";
import { formatProjectPath } from "../domain/commands";
import { describeWhen } from "../domain/when";
import { TagPill } from "./TagPill";

export type PriorityBand = "none" | "low" | "medium" | "high";

const MEDIUM_PRIORITY_FROM = 34;
const HIGH_PRIORITY_FROM = 67;

export function priorityBand(priority: number): PriorityBand {
  if (priority >= HIGH_PRIORITY_FROM) return "high";
  if (priority >= MEDIUM_PRIORITY_FROM) return "medium";
  return priority > 0 ? "low" : "none";
}

export function isFinished(view: TaskView): boolean {
  return view.status === "Done" || view.status === "Deleted";
}

interface TaskRowProps {
  view: TaskView;
  selected: boolean;
  now: Date;
  onSelect(): void;
  onOpenTag(tag: string): void;
  onOpenProject(path: ProjectPath): void;
  onToggleComplete(): void;
  onRestore(): void;
}

export function TaskRow({
  view,
  selected,
  now,
  onSelect,
  onOpenTag,
  onOpenProject,
  onToggleComplete,
  onRestore,
}: TaskRowProps) {
  const { task } = view;
  const finished = isFinished(view);
  const due = task.due === undefined ? undefined : describeWhen(task.due, now);
  const wait = view.status === "Waiting" && task.wait !== undefined ? describeWhen(task.wait, now) : undefined;
  const band = priorityBand(task.priority);

  return (
    <li
      className={`task-row${finished ? " finished" : ""}`}
      aria-selected={selected}
      data-task-id={task.id}
      onClick={onSelect}
    >
      {view.status === "Deleted" ? (
        <button
          type="button"
          className="button"
          onClick={(event) => {
            event.stopPropagation();
            onRestore();
          }}
        >
          Restore
        </button>
      ) : (
        <button
          type="button"
          role="checkbox"
          aria-checked={view.status === "Done"}
          aria-label={`Complete ${task.title}`}
          className={`check priority-${band}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleComplete();
          }}
        >
          ✓
        </button>
      )}
      <div className="task-main">
        <button type="button" className="task-open" onClick={onSelect}>
          <span className="task-title">{task.title}</span>
        </button>
        <div className="task-meta">
          {due !== undefined && <span className={`meta-${due.tone}`}>{due.text}</span>}
          {wait !== undefined && <span className="meta-later">Waits until {wait.text}</span>}
          {view.status === "Blocked" && <span className="meta-soon">Blocked</span>}
          {task.series !== undefined && <span title={seriesTitle(view)}>↻ Repeats</span>}
          {task.project.length > 0 && (
            <button
              type="button"
              className="project-crumb"
              aria-label={`Project ${formatProjectPath(task.project)}: show tasks`}
              onClick={(event) => {
                event.stopPropagation();
                onOpenProject(task.project);
              }}
            >
              {task.project.join(" › ")}
            </button>
          )}
          {task.tags.map((tag) => (
            <TagPill key={tag} tag={tag} onOpen={onOpenTag} />
          ))}
        </div>
      </div>
    </li>
  );
}

function seriesTitle(view: TaskView): string {
  const series = view.task.series;
  return series === undefined ? "" : `Instance ${series.index + 1} of a recurring series`;
}
